import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import cytoscape, { NodeSingular } from 'cytoscape';
import svg from 'cytoscape-svg';
import { saveAs } from 'file-saver';

import {
    DepositWithStandard,
    ExportSynopticCartoucheService,
} from '@connect-services/client/services/synoptic/export-synoptic-cartouche.service';

cytoscape.use(svg);

declare module 'cytoscape' {
    interface Core {
        svg(options?: { bg: string; full: boolean; scale: number }): string;
    }
}

const SVG_HEIGHT = 800; //Default height for synoptic svg
const SVG_WIDTH = 1100; //Default width for synoptic svg

@Injectable({
    providedIn: 'root',
})
export class ExportSynopticService {
    private readonly exportSynopticCartoucheService = inject(ExportSynopticCartoucheService);
    private readonly translocoService = inject(TranslocoService);

    public exportAsSvg(
        cy: cytoscape.Core,
        isFullSynoExport: boolean,
        deposit: DepositWithStandard,
        nodeName?: string,
    ): void {
        const synopticSvg = this.generateSynopticSvg(cy, isFullSynoExport);
        const dimensions = this.extractSvgDimensions(synopticSvg);

        const cartouche = this.exportSynopticCartoucheService.createSynopticCartouche(
            isFullSynoExport,
            deposit,
            dimensions,
            nodeName,
        );

        const combinedSvg = this.combineSvgWithCartouche(synopticSvg, cartouche, dimensions);

        const blob = new Blob([combinedSvg], { type: 'image/svg+xml;charset=utf-8' });

        const fileName = `${this.translocoService.translate('synopticExport.synopticExportFileName')}.svg`;

        saveAs(blob, fileName);

        return;
    }

    public exportAsSvgFromNode(nodeSelected: NodeSingular, deposit: DepositWithStandard, nodeName: string): void {
        this.hideParents(nodeSelected);

        this.exportAsSvg(nodeSelected.cy(), false, deposit, nodeName);

        this.resetView(nodeSelected);
    }

    private combineSvgWithCartouche(
        synopticSvg: string,
        cartouche: string,
        dimensions: { height: number; width: number },
    ): string {
        const totalHeight = dimensions.height + 100;

        const synopticSvgFormated = synopticSvg.replace(/^[\s\S]*?<svg[^>]*>/i, '').replace(/<\/svg>\s*$/i, '');

        return `
        <svg xmlns="http://www.w3.org/2000/svg"
             xmlns:xlink="http://www.w3.org/1999/xlink"
             width="${dimensions.width}" height="${totalHeight}"
             viewBox="0 0 ${dimensions.width} ${totalHeight}">
            <rect x="0" y="0" width="${dimensions.width}" height="${totalHeight}" fill="white" />
                ${synopticSvgFormated}
            <g transform="translate(0, ${dimensions.height})">
                ${cartouche}
            </g>
        </svg>
    `;
    }

    private extractSvgDimensions(svgContent: string): { height: number; width: number } {
        const widthHeightRegex = /<svg[^>]*width="([\d.]+)(?:px)?"[^>]*height="([\d.]+)(?:px)?"[^>]*>/;
        const viewBoxRegex = /<svg[^>]*viewBox="[^"]*0 0 ([\d.]+) ([\d.]+)"[^>]*>/;

        const dimensionMatch = svgContent.match(widthHeightRegex);
        if (dimensionMatch) {
            return {
                height: parseFloat(dimensionMatch[2]),
                width: parseFloat(dimensionMatch[1]),
            };
        }

        const viewBoxMatch = svgContent.match(viewBoxRegex);
        if (viewBoxMatch) {
            return {
                height: parseFloat(viewBoxMatch[2]),
                width: parseFloat(viewBoxMatch[1]),
            };
        }

        return { height: SVG_HEIGHT, width: SVG_WIDTH };
    }

    private generateSynopticSvg(cy: cytoscape.Core, isFullSynoExport: boolean): string {
        if (isFullSynoExport) {
            cy.fit();
        }

        const svgContent = cy.svg({ bg: 'white', full: false, scale: 1 });

        const regex = RegExp(
            /<path fill="rgb\(255,255,255\)" stroke="none" paint-order="stroke fill markers" ([^>]*)\/><g clip-path="([^"]*)"><g><image ([^>]*)\/><\/g><\/g><rect fill="(rgb\([\d]*,[\d]*,[\d]*\))"([^>]*)\/>/,
            'gmi',
        );

        return svgContent.replace(
            regex,
            '<path fill="none" stroke="$4"  paint-order="stroke fill markers" $1/><g clip-path="$2"><g><image $3/></g></g><rect fill="$4"$5/>',
        );
    }

    private hideParents(nodeSelected: NodeSingular): void {
        const cy = nodeSelected.cy();

        const nodeAndChilds = nodeSelected.union(nodeSelected.successors());

        const nodesExceptSelectedAndChilds = cy.nodes().difference(nodeAndChilds);
        nodesExceptSelectedAndChilds.addClass('hide');

        const edgesToHide = nodesExceptSelectedAndChilds.connectedEdges();
        edgesToHide.addClass('hide');

        nodeSelected.cy().fit(nodeAndChilds);
    }

    private resetView(nodeSelected: NodeSingular): void {
        const cy = nodeSelected.cy();

        cy.nodes().removeClass('hide');
        cy.edges().removeClass('hide');

        cy.fit();
    }
}
